import { startLoop } from './loop';
import { createGame, step } from './sim/sim';
import { squadLeader } from './sim/squads';
import type { CharacterId, Command, GameState } from './sim/types';
import { EMPTY_COMMAND } from './sim/types';
import { Input } from './input/input';
import { Renderer } from './render/renderer';
import type { PosSnapshot } from './render/renderer';
import { Hud } from './ui/hud';
import { Sfx } from './audio/sfx';

const app = document.getElementById('app')!;
const renderer = new Renderer(app);
const hud = new Hud(app);
const input = new Input(renderer.canvas);
const sfx = new Sfx();
renderer.canvas.addEventListener('mousedown', () => sfx.unlock());

let state = createGame();
let kingAlertUntil = 0;
let pendingResearch: string | null = null;
let pendingBuy: Command['buy'] = null;
hud.onResearch = (id) => {
  pendingResearch = id;
};
hud.onBuy = (kind) => {
  pendingBuy = kind;
};

// The sim mutates in place; for render interpolation we snapshot only what
// moves (position + heading) each tick, reusing the entries to avoid GC churn.
const prevPos = new Map<CharacterId, PosSnapshot>();
function capturePositions(s: GameState): void {
  for (const id of Object.keys(s.characters)) {
    const c = s.characters[id];
    let snap = prevPos.get(id);
    if (!snap) {
      snap = { x: c.x, z: c.z, elev: c.elev, heading: c.heading };
      prevPos.set(id, snap);
    } else {
      snap.x = c.x;
      snap.z = c.z;
      snap.elev = c.elev;
      snap.heading = c.heading;
    }
  }
}
capturePositions(state);

// ── Photo mode (dev): deterministic screenshots via URL params ──────────────
// ?cam=x,y,z,yawDeg,pitchDeg  fixed camera pose
// ?ticks=N                    fast-forward N sim ticks before first render
// ?order=advance|hold|retreat initial order for the friendly army
// ?seed=N                     sim seed
// ?hud=0                      hide the HUD overlay
const params = new URLSearchParams(location.search);
if (params.has('seed')) {
  state = createGame(Number(params.get('seed')) | 0);
  capturePositions(state);
}
if (params.has('cam')) {
  const [px, py, pz, yawDeg, pitchDeg] = params
    .get('cam')!
    .split(',')
    .map((v) => Number(v));
  renderer.fixedCam = {
    x: px,
    y: py,
    z: pz,
    yaw: ((yawDeg || 0) * Math.PI) / 180,
    pitch: ((pitchDeg || 0) * Math.PI) / 180,
  };
}
{
  const order = params.get('order');
  const ticks = Number(params.get('ticks') ?? 0);
  if (order === 'attack' || order === 'defend' || order === 'raid' || order === 'rally') {
    step(state, { ...EMPTY_COMMAND, armyPosture: order });
  }
  for (let i = 0; i < ticks; i++) step(state, EMPTY_COMMAND);
  if (order || ticks > 0) capturePositions(state);
}
const hideHud = params.get('hud') === '0';
// freeze the sim after the fast-forward (deterministic screenshots)
const photoPause = params.get('pause') === '1';

const LOOK_SENS = 0.0028;
const PITCH_MIN = 0.08;
const PITCH_MAX = 1.15;
const MAX_SOUNDS_PER_TICK = 8;

function playSounds(s: GameState): void {
  const me = s.characters[s.controlled];
  let played = 0;
  for (const e of s.events) {
    if (played >= MAX_SOUNDS_PER_TICK) break;
    const vol = Math.max(0, 1 - Math.hypot(e.x - me.x, e.z - me.z) / 35);
    if (vol <= 0.02) continue;
    played++;
    switch (e.type) {
      case 'swing':
        sfx.swing(false, vol);
        break;
      case 'swing-heavy':
        sfx.swing(true, vol);
        break;
      case 'kick':
        sfx.kick(vol);
        break;
      case 'hit':
        sfx.hit(vol);
        break;
      case 'blocked':
        sfx.block(vol);
        break;
      case 'guard-break':
        sfx.guardBreak(vol);
        break;
      case 'parry':
        sfx.parry(vol);
        break;
      case 'dodge':
        sfx.dodge(vol);
        break;
      case 'death':
        sfx.death(vol);
        break;
      case 'respawn':
        sfx.respawn(vol);
        break;
      case 'arrow':
        sfx.arrow(vol);
        break;
      case 'build':
        sfx.build(vol);
        break;
      case 'raze':
        sfx.guardBreak(vol);
        break;
    }
  }
  // alerts, alarm bells, and research have their own (wider) falloff
  for (const e of s.events) {
    if (e.type === 'king-threat') {
      sfx.alarm();
      kingAlertUntil = performance.now() + 4000;
    } else if (e.type === 'alarm') {
      const d = Math.hypot(e.x - me.x, e.z - me.z);
      sfx.bell(Math.max(0.15, 1 - d / 160));
    } else if (e.type === 'horn') {
      const d = Math.hypot(e.x - me.x, e.z - me.z);
      sfx.horn(Math.max(0.2, 1 - d / 200));
    } else if (e.type === 'research' && e.x < 0) {
      // negative X = the friendly castle's throne — our research completed
      sfx.research();
    }
  }
}

function collectCommand(): Command {
  const cmd: Command = { ...EMPTY_COMMAND };

  // camera-relative WASD -> world-space move vector
  let f = 0;
  let r = 0;
  if (input.isHeld('KeyW')) f += 1;
  if (input.isHeld('KeyS')) f -= 1;
  if (input.isHeld('KeyD')) r += 1;
  if (input.isHeld('KeyA')) r -= 1;
  // forward = (sin yaw, cos yaw); screen-right = forward x up = (-cos yaw, sin yaw)
  const yaw = renderer.cameraYaw;
  cmd.moveX = f * Math.sin(yaw) - r * Math.cos(yaw);
  cmd.moveZ = f * Math.cos(yaw) + r * Math.sin(yaw);

  const shift = input.isHeld('ShiftLeft') || input.isHeld('ShiftRight');
  cmd.sprint = shift;

  const swing = input.wasPressed('Mouse0');
  cmd.attackLight = swing && !shift;
  cmd.attackHeavy = swing && shift;
  cmd.kick = input.wasPressed('KeyF');
  cmd.dodge = input.wasPressed('Space');
  cmd.block = input.isHeld('Mouse2');
  cmd.faceYaw = yaw;

  // army posture on plain number keys
  if (input.wasPressed('Digit1')) cmd.armyPosture = 'defend';
  if (input.wasPressed('Digit2')) cmd.armyPosture = 'attack';
  if (input.wasPressed('Digit3')) cmd.armyPosture = 'raid';
  if (input.wasPressed('Digit4')) cmd.armyPosture = 'rally';

  if (pendingResearch) {
    cmd.research = pendingResearch;
    pendingResearch = null;
  }
  if (pendingBuy) {
    cmd.buy = pendingBuy;
    pendingBuy = null;
  }

  // Tab cycles every hero you can inhabit: agent, king, then squad leaders
  let switchTo: CharacterId | null = null;
  if (input.wasPressed('Tab')) {
    const cycle: CharacterId[] = [];
    for (const c of Object.values(state.characters)) {
      if (c.alive && c.brain === 'player') cycle.push(c.id);
    }
    for (const id of Object.keys(state.squads)) {
      if (state.squads[id].team !== 'friendly') continue;
      const leader = squadLeader(state, id);
      if (leader) cycle.push(leader.id);
    }
    if (cycle.length > 0) {
      const idx = cycle.indexOf(state.controlled);
      switchTo = cycle[(idx + 1) % cycle.length];
    }
  }
  cmd.switchTo = switchTo;

  return cmd;
}

let fps = 60;
let lastFrame = performance.now();

/** Closing a menu hands the mouse straight back to the camera (the keypress
 * counts as user activation, so the re-lock is allowed). */
function relockPointer(): void {
  const p = renderer.canvas.requestPointerLock() as unknown as Promise<void> | undefined;
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

startLoop({
  simTick() {
    if (photoPause) return;
    if (input.wasPressed('KeyT')) {
      hud.setTechOpen(!hud.techOpen);
      input.suppressLock = hud.techOpen;
      if (hud.techOpen) document.exitPointerLock();
      else relockPointer();
    }
    if (input.wasPressed('KeyB')) {
      hud.setBuildOpen(!hud.buildOpen);
      input.suppressLock = hud.buildOpen;
      if (hud.buildOpen) document.exitPointerLock();
      else relockPointer();
    }
    if (input.wasPressed('Enter')) {
      state = createGame((Math.random() * 0xffffffff) | 0);
      prevPos.clear();
      capturePositions(state);
      hud.setTechOpen(false);
      hud.setBuildOpen(false);
      input.endTick();
      return;
    }
    const cmd = collectCommand();
    capturePositions(state);
    step(state, cmd);
    playSounds(state);
    input.endTick();
  },
  render(alpha) {
    const now = performance.now();
    fps = fps * 0.95 + (1000 / Math.max(1, now - lastFrame)) * 0.05;
    lastFrame = now;

    const { dx, dy } = input.takeMouseDelta();
    if (input.locked) {
      renderer.cameraYaw -= dx * LOOK_SENS;
      renderer.cameraPitch = Math.min(
        PITCH_MAX,
        Math.max(PITCH_MIN, renderer.cameraPitch + dy * LOOK_SENS)
      );
    }
    renderer.render(prevPos, state, alpha);
    hud.update(state, input.locked, fps, performance.now() < kingAlertUntil);
    if (hideHud) {
      const el = document.querySelector<HTMLElement>('.hud');
      if (el) el.style.display = 'none';
    }
  },
});
